import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import styled from 'styled-components'
import { useTrainingWeeks } from '../../hooks/useTrainingWeeks'
import { useUserSettings } from '../../hooks/useUserSettings'
import { supabase } from '../../lib/supabase'
import { theme } from '../../theme/theme'
import type { TrainingWeek } from '../../types/database.tables'
import type { WorkoutWithData } from '../../types/workout'
import { formatDateForComparison, formatDateToLocalTime, formatDurationMin } from '../../utils/dateUtils'
import { getDisciplineColor } from '../../utils/disciplineColors'

const SummaryPanel = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  min-width: 280px;
  max-width: 320px;
  height: fit-content;

  @media (max-width: 768px) {
    width: 100%;
    max-width: none;
  }
`

const SummarySection = styled.div`
  margin-bottom: ${theme.spacing.lg};

  &:last-child {
    margin-bottom: 0;
  }
`

const SkeletonWrapper = styled.div`
  .react-loading-skeleton {
    --base-color: ${({ theme }) => theme.colors.surface};
    --highlight-color: ${({ theme }) => theme.colors.border};
    border-radius: ${theme.borderRadius.md};
  }
`

const DisciplineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`

const DisciplineItem = styled.div<{ $color: string; $useColors: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .discipline-name {
    font-size: ${theme.typography.sm};
    color: ${({ theme }) => theme.colors.text};
    font-weight: 500;
    text-transform: capitalize;
  }
  
  .discipline-hours {
    font-size: ${theme.typography.sm};
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`

const ProgressBar = styled.div`
  margin-top: ${theme.spacing.md};
`

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xs};
  
  .label {
    font-size: ${theme.typography.sm};
    color: ${({ theme }) => theme.colors.text};
    font-weight: 500;
  }
  
  .percentage {
    font-size: ${theme.typography.sm};
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`

const ProgressTrack = styled.div`
  background: ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  height: 8px;
  overflow: hidden;
`

const ProgressFill = styled.div<{ $percentage: number; $color?: string }>`
  height: 100%;
  width: ${(props) => Math.min(props.$percentage, 100)}%;
  background: ${({ theme, $color }) => $color || theme.colors.primary};
  border-radius: ${theme.borderRadius.sm};
  transition: width 0.3s ease;
`

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.sm};
  font-style: italic;
`

const HintMessage = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${theme.typography.xs};
  line-height: 1.5;

  strong {
    color: ${({ theme }) => theme.colors.text};
    font-weight: 600;
  }
`

const Separator = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
  margin: ${theme.spacing.sm} 0;
`

const TrainingWeekGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
`

const EditableField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`

const FieldLabel = styled.label`
  font-size: ${theme.typography.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  font-weight: 500;
`

const FieldRow = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
  align-items: center;
`

const HoursChangeIndicator = styled.span<{ $change: number; $changePercent: number }>`
  font-size: ${theme.typography.xs};
  font-weight: 600;
  padding: 2px 6px;
  border-radius: ${theme.borderRadius.sm};
  color: ${(props) => (props.$changePercent > 0.15 ? '#F44336' : theme.colors.textMuted)};
  background: ${(props) => (props.$change > 0 ? '#4CAF5020' : props.$change < 0 ? '#F4433620' : 'transparent')};
`

const InlineInput = styled.input`
  background: transparent;
  border: 1px solid transparent;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.xs};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.sm};
  font-weight: 500;
  transition: ${theme.transitions.default};
  
  &:hover, &:focus {
    background: ${({ theme }) => theme.colors.cardBackground};
    border-color: ${({ theme }) => theme.colors.primary};
    outline: none;
  }
  
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}20;
  }
`

const InlineSelect = styled.select`
  background: transparent;
  border: 1px solid transparent;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.xs};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.sm};
  font-weight: 500;
  cursor: pointer;
  transition: ${theme.transitions.default};
  
  &:hover, &:focus {
    background: ${({ theme }) => theme.colors.cardBackground};
    border-color: ${({ theme }) => theme.colors.primary};
    outline: none;
  }
  
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}20;
  }
`

const CommentsField = styled.textarea`
  background: transparent;
  border: 1px solid transparent;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.xs};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.sm};
  resize: vertical;
  min-height: 60px;
  font-family: inherit;
  transition: ${theme.transitions.default};

  &:hover, &:focus {
    background: ${({ theme }) => theme.colors.cardBackground};
    border-color: ${({ theme }) => theme.colors.primary};
    outline: none;
  }

  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
    font-style: italic;
  }
`

const BreakdownContainer = styled.div`
  margin-bottom: ${theme.spacing.sm};
`

const ClickableSectionHeader = styled.div`
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.8;
  }
`

const ZoneBreakdown = styled.div`
  margin-top: ${theme.spacing.xs};
  padding-left: ${theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`

const SportItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: ${theme.typography.xs};
    color: ${({ theme }) => theme.colors.textSecondary};

    .capital-label {
        text-transform: capitalize;
    }
`

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  display: inline-block;
  margin-right: ${theme.spacing.xs};
  transition: transform 0.2s ease;
  transform: rotate(${(props) => (props.$expanded ? '90deg' : '0deg')});
`

// Using centralized discipline color utility

interface WeekSummaryPanelProps {
  athleteId: string
  workouts: WorkoutWithData[]
  weekDates: Date[]
  targetTSS?: number
  targetHours?: number
}

interface HRZoneBySport {
  activity_type: string
  time_seconds: number
  percentage: number
}

interface HRZoneData {
  zone_id: string
  zone_name: string
  zone_color: string
  time_seconds: number
  percentage: number
  by_sport: HRZoneBySport[]
}

interface SleepData {
  min_hours: number
  avg_hours: number
  max_hours: number
  nights_count: number
  avg_start_time: number
  start_variance: number
}

interface WeeklySummary {
  completed_calories: number
  completed_workouts: number
  completed_hours: number
  workouts_progress: number
  discipline_breakdown: Record<string, { count: number; hours: number; calories: number }>
  hr_zones: HRZoneData[]
  sleep: SleepData | null
}

export const WeekSummaryPanel: React.FC<WeekSummaryPanelProps> = ({ athleteId, workouts, weekDates }) => {
  const { trainingWeek, loadTrainingWeek, updateTrainingWeek, createTrainingWeek, getPreviousWeek, isLoading } =
    useTrainingWeeks()
  const [localTrainingWeek, setLocalTrainingWeek] = useState<TrainingWeek | null>(null)
  const [previousWeek, setPreviousWeek] = useState<TrainingWeek | null>(null)
  const [weeklySummary, setWeeklySummary] = useState<WeeklySummary | null>(null)
  const [loadingSummary, setLoadingSummary] = useState(false)
  const [expandedZones, setExpandedZones] = useState<Set<string>>(new Set())
  const [showNumWorkoutsBreakdown, setShowNumWorkoutsBreakdown] = useState(false)
  const [showCaloriesBreakdown, setShowCaloriesBreakdown] = useState(false)
  // const { settings } = useUserSettings()

  // Load training week data when weekDates change
  useEffect(() => {
    if (weekDates.length > 0) {
      const startDate = weekDates[0]
      loadTrainingWeek(startDate)

      // Also load previous week for comparison
      // TODO: getPreviousWeek(startDate).then(setPreviousWeek)
    }
  }, [weekDates, loadTrainingWeek])

  // Update local state when training week is loaded
  useEffect(() => {
    setLocalTrainingWeek(trainingWeek)
  }, [trainingWeek])

  // Memoize start and end dates to prevent unnecessary refetches
  const weekDateRange = useMemo(() => {
    if (weekDates.length === 0) return null
    return {
      startDate: formatDateForComparison(weekDates[0]),
      endDate: formatDateForComparison(weekDates[weekDates.length - 1]),
    }
  }, [weekDates[0]?.toISOString(), weekDates[6]?.toISOString()])

  // Load weekly summary data when week dates change
  useEffect(() => {
    const loadWeeklySummary = async () => {
      if (!weekDateRange) return

      try {
        setLoadingSummary(true)

        const { data: sessionData } = await supabase.auth.getSession()
        if (!sessionData.session) return

        const { data, error } = await supabase.functions.invoke('weekly-summary', {
          body: {
            user_id: athleteId,
            start_date: weekDateRange.startDate,
            end_date: weekDateRange.endDate,
          },
        })
        if (error) {
          console.error('Error loading weekly summary:', error)
          return
        }
        setWeeklySummary(data)
      } catch (err) {
        console.error('Failed to load weekly summary:', err)
      } finally {
        setLoadingSummary(false)
      }
    }

    loadWeeklySummary()
  }, [weekDateRange])

  // Create training week if it doesn't exist
  const ensureTrainingWeekExists = async () => {
    if (!trainingWeek && weekDates.length > 0) {
      const startDate = weekDates[0]

      // Get previous week data to increment from
      const previousWeek = await getPreviousWeek(startDate)

      const newTrainingWeek = await createTrainingWeek({
        start_date: formatDateForComparison(startDate),
        week_in_plan: previousWeek ? previousWeek.week_in_plan + 1 : 1,
        training_phase: previousWeek ? previousWeek.training_phase : 'build',
        target_hours: previousWeek ? previousWeek.target_hours : 8.0,
        comments: null,
      })
      setLocalTrainingWeek(newTrainingWeek)
    }
  }

  // Handle field updates with automatic save
  const handleFieldUpdate = async (field: keyof TrainingWeek, value: any) => {
    if (!localTrainingWeek) {
      await ensureTrainingWeekExists()
      return
    }

    // Update local state immediately
    const updatedWeek = { ...localTrainingWeek, [field]: value }
    setLocalTrainingWeek(updatedWeek)

    // Save to database
    try {
      await updateTrainingWeek({ [field]: value })
    } catch (error) {
      console.error('Failed to update training week:', error)
      // Revert local state on error
      setLocalTrainingWeek(localTrainingWeek)
    }
  }

  // Calculate hours change from previous week
  const hoursChange =
    localTrainingWeek && previousWeek ? (localTrainingWeek.target_hours ?? 0) - (previousWeek.target_hours ?? 0) : 0

  // Toggle zone expansion
  const toggleZone = (zoneName: string) => {
    setExpandedZones((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(zoneName)) {
        newSet.delete(zoneName)
      } else {
        newSet.add(zoneName)
      }
      return newSet
    })
  }

  // Use data from edge function
  const totalCalories = weeklySummary?.completed_calories ?? 0
  const completedWorkouts = weeklySummary?.completed_workouts ?? 0
  const completedWorkoutHours = weeklySummary?.completed_hours ?? 0
  const disciplineBreakdown = weeklySummary?.discipline_breakdown ?? {}
  const hrZoneData = weeklySummary?.hr_zones ?? []

  return (
    <SummaryPanel>
      {/* Training Week Section
      <TrainingWeekSection>
        <TrainingWeekGrid>
          <EditableField>
            <FieldLabel>Week / Phase</FieldLabel>
            <FieldRow>
              <InlineInput
                type="number"
                min="1"
                value={localTrainingWeek?.week_in_plan || 1}
                onChange={(e) => handleFieldUpdate('week_in_plan', parseInt(e.target.value))}
                onBlur={() => !localTrainingWeek && ensureTrainingWeekExists()}
              />

              <InlineSelect
                  value={localTrainingWeek?.training_phase || 'build'}
                  onChange={(e) => handleFieldUpdate('training_phase', e.target.value)}
                  onBlur={() => !localTrainingWeek && ensureTrainingWeekExists()}
              >
                <option value="base">Base</option>
                <option value="build">Build</option>
                <option value="peak">Peak</option>
                <option value="taper">Taper</option>
                <option value="recovery">Recovery</option>
                <option value="maintenance">Maintenance</option>
              </InlineSelect>
            </FieldRow>
          </EditableField>
          
          <EditableField>
            <FieldLabel>Target Hours</FieldLabel>
            <FieldRow>
              <InlineInput
                type="number"
                min="0"
                step="0.5"
                value={localTrainingWeek?.target_hours}
                onChange={(e) => handleFieldUpdate('target_hours', parseFloat(e.target.value))}
                onBlur={() => !localTrainingWeek && ensureTrainingWeekExists()}
              />
              {previousWeek && hoursChange !== 0 && (
                <HoursChangeIndicator $change={hoursChange} $changePercent={hoursChange / previousWeek.target_hours}>
                  {hoursChange > 0 ? '+' : ''}{hoursChange}h
                </HoursChangeIndicator>
              )}
            </FieldRow>
          </EditableField>
        </TrainingWeekGrid>
        
        <EditableField>
          <FieldLabel>Comments</FieldLabel>
          <CommentsField
            value={localTrainingWeek?.comments || ''}
            onChange={(e) => handleFieldUpdate('comments', e.target.value || null)}
            onBlur={() => !localTrainingWeek && ensureTrainingWeekExists()}
            placeholder="Add notes about this training week..."
          />
        </EditableField>
      </TrainingWeekSection>
      <Separator/>
       */}

      {/* Total Workouts */}
      <SummarySection>
        {loadingSummary ? (
          <SkeletonWrapper>
            <Skeleton height={40} style={{ marginBottom: 8 }} />
            <Skeleton height={40} />
          </SkeletonWrapper>
        ) : (
          <ClickableSectionHeader onClick={() => setShowNumWorkoutsBreakdown(!showNumWorkoutsBreakdown)}>
            <ProgressBar>
              <ProgressLabel>
                <span className="label">
                  <ExpandIcon $expanded={showNumWorkoutsBreakdown}>▶</ExpandIcon>
                  {completedWorkouts} Workout{completedWorkouts === 1 ? '' : 's'}
                </span>
                <span className="percentage">{formatDurationMin(completedWorkoutHours * 60)}</span>
              </ProgressLabel>
            </ProgressBar>
          </ClickableSectionHeader>
        )}
        {/* Workouts by Discipline */}
        {loadingSummary ? (
          <SkeletonWrapper>
            <Skeleton height={24} count={3} style={{ marginBottom: 8 }} />
          </SkeletonWrapper>
        ) : (
          <BreakdownContainer>
            {showNumWorkoutsBreakdown && (
              <ZoneBreakdown>
                {Object.entries(disciplineBreakdown)
                  .sort(([, a], [, b]) => b.count - a.count) // Sort by count descending
                  .map(([discipline, summary]) => (
                    <SportItem key={discipline}>
                      <span className="capital-label">
                        {summary.count}x {discipline}
                      </span>
                      <span className="percentage">{formatDurationMin(summary.hours * 60)}</span>
                    </SportItem>
                  ))}
              </ZoneBreakdown>
            )}
          </BreakdownContainer>
        )}
      </SummarySection>

      {/* Total Load */}
      <SummarySection>
        {loadingSummary ? (
          <SkeletonWrapper>
            <Skeleton height={40} style={{ marginBottom: 8 }} />
            <Skeleton height={40} />
          </SkeletonWrapper>
        ) : (
          <ClickableSectionHeader onClick={() => setShowCaloriesBreakdown(!showCaloriesBreakdown)}>
            <ProgressBar>
              <ProgressLabel>
                <span className="label">
                  <ExpandIcon $expanded={showCaloriesBreakdown}>▶</ExpandIcon>
                  Work
                </span>
                <span className="percentage">{totalCalories} cal</span>
              </ProgressLabel>
            </ProgressBar>
          </ClickableSectionHeader>
        )}

        {/* Workouts by Discipline */}
        {loadingSummary ? (
          <SkeletonWrapper>
            <Skeleton height={24} count={3} style={{ marginBottom: 8 }} />
          </SkeletonWrapper>
        ) : (
          <BreakdownContainer>
            {showCaloriesBreakdown && (
              <ZoneBreakdown>
                {Object.entries(disciplineBreakdown)
                  .sort(([, a], [, b]) => b.count - a.count) // Sort by count descending
                  .map(([discipline, summary]) => (
                    <SportItem key={discipline}>
                      <span className="capital-label">{discipline}</span>
                      <span className="percentage">{summary.calories} cal</span>
                    </SportItem>
                  ))}
              </ZoneBreakdown>
            )}
          </BreakdownContainer>
        )}
      </SummarySection>

      {/* Sleep */}
      {weeklySummary?.sleep && (
        <>
          <Separator />

          <SummarySection>
            <ProgressBar>
              <ProgressLabel>
                <span className="label">Avg. Sleep</span>
                <span className="percentage">
                  {formatDurationMin(weeklySummary.sleep.avg_hours * 60)}
                  {/*}
                    {weeklySummary.sleep.min_hours !== weeklySummary.sleep.max_hours && (
                      <>
                        {' '}
                        ({formatDurationMin(weeklySummary.sleep.min_hours * 60)} -{' '}
                        {formatDurationMin(weeklySummary.sleep.max_hours * 60)})
                      </>
                    )}
                    */}
                </span>
              </ProgressLabel>
              <ProgressTrack>
                <ProgressFill
                  $percentage={(weeklySummary.sleep.avg_hours / 7) * 100}
                  $color={
                    weeklySummary.sleep.avg_hours >= 7
                      ? theme.colors.success
                      : weeklySummary.sleep.avg_hours >= 6
                        ? theme.colors.warning
                        : theme.colors.error
                  }
                />
              </ProgressTrack>
            </ProgressBar>

            <ProgressBar>
              <ProgressLabel>
                <span className="label">Bed Time</span>
                <span className="percentage">
                  ±{formatDurationMin(weeklySummary.sleep.start_variance)} (
                  {formatDateToLocalTime(new Date(weeklySummary.sleep.avg_start_time))})
                </span>
              </ProgressLabel>
              <ProgressTrack>
                <ProgressFill
                  $percentage={Math.max(0, Math.min(100, 100 - (weeklySummary.sleep.start_variance / 60) * 100))}
                  $color={
                    weeklySummary.sleep.start_variance <= 15
                      ? theme.colors.success
                      : weeklySummary.sleep.start_variance <= 45
                        ? theme.colors.warning
                        : theme.colors.error
                  }
                />
              </ProgressTrack>
            </ProgressBar>
          </SummarySection>
        </>
      )}

      <Separator />

      {/* Time in Heart Rate Zones */}
      <SummarySection>
        {loadingSummary ? (
          <SkeletonWrapper>
            <Skeleton height={32} count={5} style={{ marginBottom: 8 }} />
          </SkeletonWrapper>
        ) : hrZoneData.length === 0 && completedWorkouts > 0 ? (
          <HintMessage>
            <strong>No heart rate zones configured</strong>
            <p>Configure heart rate zones in athlete settings to see weekly time-in-zone distribution.</p>
          </HintMessage>
        ) : hrZoneData.length === 0 ? (
          <></>
        ) : (
          hrZoneData.map((zone) => {
            const isExpanded = expandedZones.has(zone.zone_name)
            const hasSports = zone.by_sport && zone.by_sport.length > 0

            return (
              <BreakdownContainer key={zone.zone_name}>
                <ClickableSectionHeader onClick={() => hasSports && toggleZone(zone.zone_name)}>
                  <ProgressBar>
                    <ProgressLabel>
                      <span className="label">
                        {hasSports && <ExpandIcon $expanded={isExpanded}>▶</ExpandIcon>}
                        {zone.zone_name}
                      </span>
                      <span className="percentage">{formatDurationMin(zone.time_seconds / 60)}</span>
                    </ProgressLabel>
                    <ProgressTrack>
                      <ProgressFill $percentage={zone.percentage} $color={zone.zone_color} />
                    </ProgressTrack>
                  </ProgressBar>
                </ClickableSectionHeader>

                {isExpanded && hasSports && (
                  <ZoneBreakdown>
                    {zone.by_sport.map((sport) => (
                      <SportItem key={sport.activity_type}>
                        <span className="capital-label">{sport.activity_type}</span>
                        <span>{formatDurationMin(sport.time_seconds / 60)}</span>
                      </SportItem>
                    ))}
                  </ZoneBreakdown>
                )}
              </BreakdownContainer>
            )
          })
        )}
      </SummarySection>
    </SummaryPanel>
  )
}
